import { useEffect } from "react";
import styled from "styled-components";
import { MARKETING } from "../theme/ThemeContext";
import { TERMS_MD, PRIVACY_MD } from "../legal/content";
import renderMarkdown from "../legal/renderMarkdown";

const C = MARKETING;
const LOGO = "/assets/scoutm8-landing-logo.png";
const SANS = "'Geist', system-ui, -apple-system, 'Segoe UI', sans-serif";

const DOCS = {
  terms: { title: "Terms & Conditions", md: TERMS_MD, other: { href: "/privacy-policy", label: "Privacy Policy" } },
  privacy: { title: "Privacy Policy", md: PRIVACY_MD, other: { href: "/terms", label: "Terms & Conditions" } },
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.onDark};
  font-family: ${SANS};
  color: ${C.ink};
`;

const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 100;
  background: color-mix(in srgb, ${C.onDark} 86%, transparent);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid ${C.hair};
`;

const NavInner = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 44px;
  height: 74px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 560px) {
    padding: 0 20px;
    height: 64px;
  }
`;

const NavLink = styled.a`
  font-size: 14px;
  color: ${C.body};
  text-decoration: none;
  transition: color 0.18s ease;

  &:hover {
    color: ${C.blue};
  }
`;

const Article = styled.article`
  max-width: 820px;
  margin: 0 auto;
  padding: 56px 24px 80px;
  font-size: 15.5px;
  line-height: 1.65;
  color: ${C.bodyStrong};

  h1 {
    font-size: 36px;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: ${C.ink};
    margin: 0 0 6px;
  }
  h1 + p {
    color: ${C.muted};
    font-size: 14px;
    margin-bottom: 36px;
  }
  h2 {
    font-size: 21px;
    letter-spacing: -0.01em;
    color: ${C.ink};
    margin: 40px 0 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid ${C.hair};
  }
  p {
    margin: 0 0 14px;
  }
  strong {
    color: ${C.ink};
    font-weight: 600;
  }
  a {
    color: ${C.blue};
    text-decoration: none;
  }
  a:hover {
    text-decoration: underline;
  }
  ul {
    margin: 0 0 16px;
    padding-left: 22px;
  }
  li {
    margin-bottom: 6px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0 20px;
    font-size: 14px;
  }
  th,
  td {
    border: 1px solid ${C.line};
    padding: 8px 10px;
    vertical-align: top;
    text-align: left;
  }
  th {
    background: ${C.wash};
    color: ${C.ink};
    font-weight: 600;
  }

  @media (max-width: 560px) {
    padding: 36px 20px 60px;
    font-size: 15px;
    h1 {
      font-size: 28px;
    }
    table {
      display: block;
      overflow-x: auto;
    }
  }
`;

const Footer = styled.footer`
  border-top: 1px solid ${C.hair};
  padding: 24px 44px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12.5px;
  color: ${C.muted};

  a {
    color: ${C.body};
    text-decoration: none;
    margin-left: 18px;
  }
  a:hover {
    color: ${C.blue};
  }

  @media (max-width: 560px) {
    padding: 20px;
    flex-direction: column;

    /* Stacked under the copyright line, the first link's spacer reads as an indent */
    a:first-child {
      margin-left: 0;
    }
  }
`;

export default function LegalPage({ doc }) {
  const { title, md, other } = DOCS[doc];

  useEffect(() => {
    const prev = document.title;
    document.title = `${title} · scoutm8`;
    window.scrollTo(0, 0);
    return () => { document.title = prev; };
  }, [title]);

  return (
    <Page>
      <Nav>
        <NavInner>
          <a href="/" style={{ display: "flex", alignItems: "center" }}>
            <img src={LOGO} alt="scoutm8" style={{ height: 30, width: "auto", display: "block" }} />
          </a>
          <NavLink href="/">← Back to home</NavLink>
        </NavInner>
      </Nav>
      <Article>{renderMarkdown(md)}</Article>
      <Footer>
        <span>© {new Date().getFullYear()} scoutm8. All rights reserved.</span>
        <span>
          <a href="/">Home</a>
          <a href={other.href}>{other.label}</a>
        </span>
      </Footer>
    </Page>
  );
}
